import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";

import { MatDialog } from "@angular/material/dialog";

import { TranslateService } from "@ngx-translate/core";

import { BehaviorSubject, EMPTY, Observable, Subscription } from "rxjs";
import { catchError, finalize, map, shareReplay } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import {
  createFuelProductList,
  getSiteFuelProductList,
} from "src/app/store/actions/sites/sites.actions";
import {
  AppState,
  loadedTenantState,
  siteFuelProductListLoadedState,
} from "src/app/store/reducers";

import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";
import { ProductService } from "src/app/shared/services/products/products/product.service";

import { SiteProductDTO } from "src/app/models/Sites/siteProductDTO";
import { ProductDTO } from "src/app/models/Products/productDTO";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { FieldType, FilterName } from "src/app/models/custom-table/custom-table.model";
import {
  Dialog,
  GuardConfirmationDialogData,
} from "src/app/models/dialog/dialog.model";
import { AutocompleteSelectItem } from "src/app/models/selection-list/autocomplete-select-item.model";

import { FormUtils } from "src/app/shared/utils/formUtils";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";

import { BaseSiteProducts } from "src/app/shared/base-classes/base-site-products/base-site-products";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { CanComponentDeactivate } from "src/app/shared";

// Product Detail Form
const PRODUCT_FORM_PARAMS = {
  productList: "productList",
  siteCode: "siteCode",
  product: "product",
  productDescription: "productDescription",
  fccProductCode: "fccProductCode",
};

@Component({
  selector: "site-products",
  templateUrl: "./products.component.html",
  styleUrls: ["./products.component.scss"],
})
export class ProductsComponent
  extends BaseSiteProducts
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  @Input() set siteCode(code: string) {
    if (code) {
      this._siteCode = code;
      this.createProductForm();
      this.loadSiteProductList(this._siteCode);
    }
  }
  @Input() duplication: boolean;

  @Output() getProductsForm: EventEmitter<FormGroup> =
    new EventEmitter<FormGroup>();

  _siteCode: string;

  // Site Product List
  siteFuelProductList$: Observable<SiteProductDTO[]>;
  siteFuelProductList: SiteProductDTO[];
  siteFuelProductListSub: Subscription;

  // Product Search
  isLoadingProducts$ = new BehaviorSubject<boolean>(false);
  productList$: Observable<ProductDTO[]>;
  productList: AutocompleteSelectItem[] = [];
  searchTimer: number;

  // Tenant Data
  tenantDataSub: Subscription;
  tenantData$: Observable<any>;
  tenantDataFormat: any;

  isFuelFilter = {
    column: "isFuel",
    fieldType: FieldType.BOOLEAN,
    operator: FilterName.EQUAL,
    value: true
  };

  productDataForm: FormGroup;

  constructor(
    private router: Router,
    private store: Store<AppState>,
    private fb: FormBuilder,
    private translate: TranslateService,
    private snackbarService: SnackbarService,
    private productService: ProductService,
    public dialog: MatDialog
  ) {
    super(dialog);

    this.tenantData$ = this.store.pipe(select(loadedTenantState));
    this.siteFuelProductList$ = this.store.pipe(select(siteFuelProductListLoadedState));
  }

  ngOnInit() {
    this.loadTenantDateFomat();
  }

  loadTenantDateFomat() {
    this.tenantDataSub = this.tenantData$.subscribe((data: any) => {
      if (data) {
        this.tenantDataFormat = data;
      }
    });
  }

  loadSiteProductList(code: string) {
    if (this.duplication) {
      return;
    }
    this.siteFuelProductListSub = this.siteFuelProductList$.subscribe((productList) => {
      if (productList) {
        this.populateProducts(productList);
      }
    });
    this.store.dispatch(getSiteFuelProductList({ code }));
  }

  createProductForm() {
    this.productDataForm = this.fb.group({
      [PRODUCT_FORM_PARAMS.productList]: this.fb.array([]),
    });
  }

  populateProducts(productList: SiteProductDTO[]) {
    const products = this.getFormArray(
      this.productDataForm,
      PRODUCT_FORM_PARAMS.productList
    );
    this.clearFormArray(products);
    productList.forEach((product) => {
      const productFormGroup = this.createProductFormGroup(product);
      products.push(productFormGroup);
    });
  }

  createProductFormGroup(product?: SiteProductDTO): FormGroup {
    const { siteCode, productCode, productDescription, fccProductCode, unitPriceGross } =
      product || {};
    return this.fb.group({
      [PRODUCT_FORM_PARAMS.siteCode]: [siteCode ?? this._siteCode],
      [PRODUCT_FORM_PARAMS.product]: [
        productCode
          ? { code: productCode, description: productDescription, unitPriceGross }
          : null,
        [Validators.required, FormUtils.isObjectValidator],
      ],
      [PRODUCT_FORM_PARAMS.fccProductCode]: [
        fccProductCode ?? null,
        [Validators.required, Validators.min(1), Validators.max(255)],
      ],
    });
  }

  getProductsFormPristine() {
    this.getProductsForm.emit(this.productDataForm);
  }

  addProduct() {
    const products = this.getFormArray(
      this.productDataForm,
      PRODUCT_FORM_PARAMS.productList
    );
    const productFormGroup = this.createProductFormGroup();
    products.push(productFormGroup);
    this.productDataForm.markAsDirty();
  }

  openRemoveDialog(index: number) {
    this.openDefaultDialog(index, this.removeProduct.bind(this));
  }

  removeProduct(index: number) {
    const products = this.getFormArray(
      this.productDataForm,
      PRODUCT_FORM_PARAMS.productList
    );
    products.removeAt(index);
    this.productDataForm.markAsDirty();
    this.getProductsFormPristine();
  }

  validateProductSearch() {
      this.searchProducts('');
  }

  searchProducts(event: KeyboardEvent | string, hasTimer = true): void {
    const searchTerm = typeof event === 'string' ? event : (event.target as HTMLInputElement).value;

    clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(
      () => {
        this.isLoadingProducts$.next(true);
        this.productList$ = this.productService
          .getProductSearch(
            null, 
            {
              filterConfig: [{ field: "code" }, { field: "description" }],
              searchTerms: [searchTerm],
            },
            null,
            { 
              advancedFilter: [{ column: "isFuel", operator: FilterName.EQUALS_TO, value: true }]
            },
          ).pipe(
            map(({ items }) => items),
            catchError((error) => {
              this.snackbarService.show({
                message: this.translate.instant("Products.loadError"),
                type: SnackbarType.WARNING,
              });
              return EMPTY;
            }),
            finalize(() => this.isLoadingProducts$.next(false)),
            shareReplay()
          );
      },
      hasTimer ? 500 : 0
    );
  }

  displayProduct(product: { code: string; description: string }): string {
    return product?.description ?? "";
  }

  saveProductList() {
    if (this.productDataForm.pristine) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    if (!this.productDataForm.valid) {
      this.productDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant(
          "SiteManagement.SiteProductFormInvalid"
        ),
        type: SnackbarType.ERROR,
      });
      return;
    }

    const siteProductList: SiteProductDTO[] = this.productDataForm
      .get(PRODUCT_FORM_PARAMS.productList)
      .value.map((siteProduct) => {
        return {
          siteCode: siteProduct.siteCode,
          productCode: siteProduct.product.code,
          fccProductCode: siteProduct.fccProductCode.toString(),
        };
      });

    if (this.repeatedProducts(siteProductList)) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.RepeatRule"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    if (siteProductList) {
      this.store.dispatch(
        createFuelProductList({
          siteCode: this._siteCode,
          productList: siteProductList,
        })
      );
      this.productDataForm.markAsUntouched();
      this.productDataForm.markAsPristine();
      this.getProductsFormPristine();
    }
  }

  repeatedProducts(siteProductList: SiteProductDTO[]): boolean {
    return siteProductList.some((productEl) => {
      const filteredProducts = siteProductList.filter(
        (product) =>
          product.productCode === productEl.productCode ||
          product.fccProductCode === productEl.fccProductCode
      );
      return filteredProducts.length > 1;
    });
  }

  ngOnDestroy() {
    if (this.siteFuelProductListSub) {
      this.siteFuelProductListSub.unsubscribe();
    }
    if (this.tenantDataSub) {
      this.tenantDataSub.unsubscribe();
    }
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (!isAuthenticated(this.router.url) || this.productDataForm.pristine) {
      return true;
    } else {
      return false;
    }
  }
  confirmDialog(dialog: any): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    return dialogRef.afterClosed();
  }
}
